/**
 * Inventory transport boundary.
 *
 * Every request/response alias below comes from the generated canonical
 * OpenAPI client (`@erp/api-client`); nothing here hand-writes a wire type.
 * The app's HTTP layer owns credentials, acting-branch provenance,
 * Idempotency-Key minting and If-Match headers — this module only binds those
 * mechanics to the inventory operation set.
 *
 * Every inventory route is `x-branch-scoped: true`, so all calls go through
 * the operational variant which stamps the shell's acting branch.
 */
import type { components, operations } from "@erp/api-client";

import {
  apiFetchOperational as apiFetch,
  apiRequestOperational as apiRequest,
  type Page,
} from "@/lib/api/http";
import { ApiError } from "@/lib/api/errors";
import { collectCursorPages, fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

/** Wire scale of every inventory quantity: NUMERIC(20,6). */
export const QUANTITY_FRACTION_DIGITS = 6;
/** Wire scale of every inventory unit cost: NUMERIC(20,6). */
export const UNIT_COST_FRACTION_DIGITS = 6;
/** Wire scale of every inventory value/amount: NUMERIC(20,4). */
export const AMOUNT_FRACTION_DIGITS = 4;

// ── Warehouses & locations ───────────────────────────────────────────────

export type Warehouse = Schemas["InventoryWarehouse"];
export type WarehouseCreate = Schemas["InventoryWarehouseCreate"];
export type WarehousePatch = Schemas["InventoryWarehousePatch"];
export type WarehouseStatus = Schemas["InventoryWarehouseStatus"];
export type WarehouseLocation = Schemas["InventoryWarehouseLocation"];
export type WarehouseLocationCreate = Schemas["InventoryWarehouseLocationCreate"];
export type WarehouseLocationPatch = Schemas["InventoryWarehouseLocationPatch"];
export type WarehouseLocationType = Schemas["InventoryWarehouseLocationType"];
export type WarehouseLocationStatus = Schemas["InventoryWarehouseLocationStatus"];

export const WAREHOUSE_STATUSES = [
  "active",
  "archived",
] as const satisfies readonly WarehouseStatus[];
export const LOCATION_TYPES = [
  "pick",
  "bulk",
  "receiving",
  "transit",
] as const satisfies readonly WarehouseLocationType[];
export const LOCATION_STATUSES = [
  "active",
  "inactive",
] as const satisfies readonly WarehouseLocationStatus[];

// ── Stock levels & movements ─────────────────────────────────────────────

export type StockLevel = Schemas["InventoryStockLevel"];
export type StockMovement = Schemas["InventoryStockMovement"];
export type StockMovementType = Schemas["InventoryStockMovementType"];
export type StockMovementSourceType = Schemas["InventoryStockMovementSourceType"];
export type StockMovementRequest = Schemas["InventoryStockMovementRequest"];
export type ManualReceiptCommand = Schemas["InventoryManualReceiptCommand"];
export type ManualIssueCommand = Schemas["InventoryManualIssueCommand"];
export type ManualAdjustmentCommand = Schemas["InventoryManualAdjustmentCommand"];
export type ManualOpeningBalanceCommand = Schemas["InventoryManualOpeningBalanceCommand"];

export const MOVEMENT_TYPES = [
  "receipt",
  "issue",
  "transfer_out",
  "transfer_in",
  "adjustment",
  "count_variance",
  "revaluation",
] as const satisfies readonly StockMovementType[];

export const MOVEMENT_SOURCE_TYPES = [
  "purchasing_grn",
  "purchasing_landed_cost",
  "sales_shipment",
  "sales_return",
  "inventory_adjustment",
  "inventory_transfer",
  "inventory_count",
  "inventory_opening_balance",
  "inventory_movement_reversal",
] as const satisfies readonly StockMovementSourceType[];

/** Operator intents the manual command union publishes, in form order. */
export const MANUAL_MOVEMENT_KINDS = ["receipt", "issue", "adjustment", "opening_balance"] as const;
export type ManualMovementKind = (typeof MANUAL_MOVEMENT_KINDS)[number];

/** Closed reason-code list per manual command — never a free-text note. */
export const MANUAL_REASON_CODES: Readonly<Record<ManualMovementKind, readonly string[]>> = {
  receipt: ["manual_receipt", "found_stock", "customer_return_unlinked"],
  issue: ["manual_issue", "damage", "shrinkage", "internal_consumption"],
  adjustment: ["cycle_correction", "data_correction", "uom_correction"],
  opening_balance: ["opening_balance", "migration_reconciliation"],
};

// ── Transfers ────────────────────────────────────────────────────────────

export type Transfer = Schemas["InventoryTransfer"];
export type TransferSummary = Schemas["InventoryTransferSummary"];
export type TransferStatus = Schemas["InventoryTransferStatus"];
export type TransferLineInput = Schemas["InventoryTransferLineInput"];
export type TransferDocumentCreate = Schemas["InventoryTransferDocumentCreate"];
export type TransferEntryCreate = Schemas["InventoryTransferEntryCreate"];
export type TransferCreate = Schemas["InventoryTransferCreate"];
export type TransferCreateResult = Schemas["InventoryTransferCreateResult"];
export type TransferPatch = Schemas["InventoryTransferPatch"];
export type TransferPostingMode = Schemas["InventoryTransferPostingMode"];
export type TransferPostingRequest = Schemas["InventoryTransferPostingRequest"];

export const TRANSFER_STATUSES = [
  "draft",
  "in_transit",
  "received",
  "cancelled",
] as const satisfies readonly TransferStatus[];
export const TRANSFER_MODES = [
  "immediate",
  "in_transit",
  "draft",
] as const satisfies readonly TransferPostingMode[];

// ── Cycle counts ─────────────────────────────────────────────────────────

export type CountWorksheet = Schemas["InventoryCountWorksheet"];
export type CountWorksheetReview = Schemas["InventoryCountWorksheetReview"];
export type CountWorksheetDetail = Schemas["InventoryCountWorksheetDetail"];
export type CountWorksheetSummary = Schemas["InventoryCountWorksheetSummary"];
export type CountWorksheetCreate = Schemas["InventoryCountWorksheetCreate"];
export type CountWorksheetStatus = Schemas["InventoryCountWorksheetStatus"];
export type CountLineBlind = Schemas["InventoryCountLineBlind"];
export type CountLineReview = Schemas["InventoryCountLineReview"];
export type CountEntryBatch = Schemas["InventoryCountEntryBatch"];
export type CountEntryBatchResult = Schemas["InventoryCountEntryBatchResult"];
export type CountLineEntry = Schemas["InventoryCountLineEntry"];
export type CountApprovalRequest = Schemas["InventoryCountApprovalRequest"];
export type CountApprovalResult = Schemas["InventoryCountApprovalResult"];

export const COUNT_STATUSES = [
  "draft",
  "counting",
  "submitted",
  "approved",
  "cancelled",
] as const satisfies readonly CountWorksheetStatus[];

// ── Analytics ────────────────────────────────────────────────────────────

export type ReportGroupBy = Schemas["InventoryReportGroupBy"];
export type BucketDefinition = Schemas["InventoryAnalysisBucketDefinition"];
export type MovementBucketValue = Schemas["InventoryMovementBucketValue"];
export type DeadStockPage = Schemas["InventoryDeadStockPage"];
export type DeadStockWarehouseItem = Schemas["InventoryDeadStockWarehouseItem"];
export type DeadStockBranchItem = Schemas["InventoryDeadStockBranchItem"];
export type DeadStockItem = DeadStockWarehouseItem | DeadStockBranchItem;
export type SlowMoverPage = Schemas["InventorySlowMoverPage"];
export type SlowMoverWarehouseItem = Schemas["InventorySlowMoverWarehouseItem"];
export type SlowMoverBranchItem = Schemas["InventorySlowMoverBranchItem"];
export type SlowMoverItem = SlowMoverWarehouseItem | SlowMoverBranchItem;
export type VariantStats = Schemas["InventoryVariantStats"];
export type Batch = Schemas["InventoryBatch"];

export const REPORT_GROUP_BYS = ["warehouse", "branch"] as const satisfies readonly ReportGroupBy[];

// ── Query aliases ────────────────────────────────────────────────────────

export type WarehouseListQuery = NonNullable<
  operations["listInventoryWarehouses"]["parameters"]["query"]
>;
export type LocationListQuery = NonNullable<
  operations["listInventoryWarehouseLocations"]["parameters"]["query"]
>;
export type StockLevelQuery = NonNullable<
  operations["listInventoryStockLevels"]["parameters"]["query"]
>;
export type MovementListQuery = NonNullable<
  operations["listInventoryStockMovements"]["parameters"]["query"]
>;
export type TransferListQuery = NonNullable<
  operations["listInventoryTransfers"]["parameters"]["query"]
>;
export type CountListQuery = NonNullable<
  operations["listInventoryCountWorksheets"]["parameters"]["query"]
>;
export type DeadStockQuery = operations["getInventoryDeadStockReport"]["parameters"]["query"];
export type DeadStockExportQuery =
  operations["exportInventoryDeadStockReport"]["parameters"]["query"];
export type SlowMoverQuery = operations["getInventorySlowMoversReport"]["parameters"]["query"];
export type VariantStatsQuery = NonNullable<
  operations["getInventoryVariantStats"]["parameters"]["query"]
>;
export type BatchLookupQuery = NonNullable<
  operations["lookupInventoryBatches"]["parameters"]["query"]
>;

type WarehousePage = Schemas["InventoryWarehousePage"];
type LocationPage = Schemas["InventoryWarehouseLocationPage"];
type StockLevelPage = Schemas["InventoryStockLevelPage"];
type MovementPage = Schemas["InventoryStockMovementPage"];
type TransferPage = Schemas["InventoryTransferPage"];
type CountPage = Schemas["InventoryCountWorksheetPage"];
type BatchPage = Schemas["InventoryBatchPage"];

type GeneratedPage<T> = Schemas["PageEnvelope"] & { readonly items: readonly T[] };

/** The generated envelopes are deeply readonly; DataTable consumes mutables. */
function toMutablePage<T>(page: GeneratedPage<T>): Page<T> {
  return { items: [...page.items], next_cursor: page.next_cursor };
}

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

// ── Warehouses ───────────────────────────────────────────────────────────

export async function listWarehouses(
  query: WarehouseListQuery,
  signal?: AbortSignal,
): Promise<Page<Warehouse>> {
  return toMutablePage(
    await apiFetch<WarehousePage>("/warehouses", { query: { ...query }, signal }),
  );
}

/** Bounded traversal for warehouse pickers (filters, forms). */
export async function listAllWarehouses(signal?: AbortSignal): Promise<Warehouse[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<WarehousePage>("/warehouses", {
        query: { limit: 200, status: "active", ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getWarehouse(warehouseId: string): Promise<WithEtag<Warehouse>> {
  return fetchWithEtag<Warehouse>(`/warehouses/${warehouseId}`);
}

export function createWarehouse(
  input: WarehouseCreate,
  idempotencyKey: string,
): Promise<Warehouse> {
  return apiFetch<Warehouse>("/warehouses", { method: "POST", body: input, idempotencyKey });
}

export async function updateWarehouse(
  warehouseId: string,
  patch: WarehousePatch,
  ifMatch: string,
): Promise<WithEtag<Warehouse>> {
  const { data, etag } = await apiRequest<Warehouse>(`/warehouses/${warehouseId}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

/** Archive (never a physical delete): 204, guarded by `warehouse_has_movements`. */
export function archiveWarehouse(warehouseId: string, ifMatch: string): Promise<void> {
  return apiFetch<void>(`/warehouses/${warehouseId}`, {
    method: "DELETE",
    ifMatch,
    responseBody: "none",
  });
}

// ── Warehouse locations ──────────────────────────────────────────────────

export async function listLocations(
  warehouseId: string,
  query: LocationListQuery,
  signal?: AbortSignal,
): Promise<Page<WarehouseLocation>> {
  return toMutablePage(
    await apiFetch<LocationPage>(`/warehouses/${warehouseId}/locations`, {
      query: { ...query },
      signal,
    }),
  );
}

/** Bounded traversal for location pickers (movement/transfer forms). */
export async function listAllLocations(
  warehouseId: string,
  signal?: AbortSignal,
): Promise<WarehouseLocation[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<LocationPage>(`/warehouses/${warehouseId}/locations`, {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getLocation(
  warehouseId: string,
  locationId: string,
): Promise<WithEtag<WarehouseLocation>> {
  return fetchWithEtag<WarehouseLocation>(`/warehouses/${warehouseId}/locations/${locationId}`);
}

export function createLocation(
  warehouseId: string,
  input: WarehouseLocationCreate,
  idempotencyKey: string,
): Promise<WarehouseLocation> {
  return apiFetch<WarehouseLocation>(`/warehouses/${warehouseId}/locations`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export async function updateLocation(
  warehouseId: string,
  locationId: string,
  patch: WarehouseLocationPatch,
  ifMatch: string,
): Promise<WithEtag<WarehouseLocation>> {
  const { data, etag } = await apiRequest<WarehouseLocation>(
    `/warehouses/${warehouseId}/locations/${locationId}`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

/** Inactivate (never a physical delete): 204, guarded by `location_has_stock`. */
export function deactivateLocation(
  warehouseId: string,
  locationId: string,
  ifMatch: string,
): Promise<void> {
  return apiFetch<void>(`/warehouses/${warehouseId}/locations/${locationId}`, {
    method: "DELETE",
    ifMatch,
    responseBody: "none",
  });
}

// ── Stock levels ─────────────────────────────────────────────────────────

export async function listStockLevels(
  query: StockLevelQuery,
  signal?: AbortSignal,
): Promise<Page<StockLevel>> {
  return toMutablePage(
    await apiFetch<StockLevelPage>("/stock-levels", { query: { ...query }, signal }),
  );
}

// ── Stock movements ──────────────────────────────────────────────────────

export async function listMovements(
  query: MovementListQuery,
  signal?: AbortSignal,
): Promise<Page<StockMovement>> {
  return toMutablePage(
    await apiFetch<MovementPage>("/stock-movements", { query: { ...query }, signal }),
  );
}

export function getMovement(movementId: string): Promise<StockMovement> {
  return apiFetch<StockMovement>(`/stock-movements/${movementId}`);
}

/**
 * Append one operator movement. The command union is closed: the service
 * derives ledger provenance (`source_type`/`source_id`) — callers never send
 * source UUIDs, notes or other free text.
 */
export function createMovement(
  command: StockMovementRequest,
  idempotencyKey: string,
): Promise<StockMovement> {
  return apiFetch<StockMovement>("/stock-movements", {
    method: "POST",
    body: command,
    idempotencyKey,
  });
}

// ── Transfers ────────────────────────────────────────────────────────────

export async function listTransfers(
  query: TransferListQuery,
  signal?: AbortSignal,
): Promise<Page<TransferSummary>> {
  return toMutablePage(
    await apiFetch<TransferPage>("/stock-transfers", { query: { ...query }, signal }),
  );
}

export function getTransfer(transferId: string): Promise<WithEtag<Transfer>> {
  return fetchWithEtag<Transfer>(`/stock-transfers/${transferId}`);
}

export function createTransfer(
  input: TransferCreate,
  idempotencyKey: string,
): Promise<TransferCreateResult> {
  return apiFetch<TransferCreateResult>("/stock-transfers", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export async function updateTransfer(
  transferId: string,
  patch: TransferPatch,
  ifMatch: string,
): Promise<WithEtag<Transfer>> {
  const { data, etag } = await apiRequest<Transfer>(`/stock-transfers/${transferId}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

async function postTransferTransition(
  transferId: string,
  action: "ship" | "receive" | "cancel",
  body: TransferPostingRequest | undefined,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<Transfer>> {
  const { data, etag } = await apiRequest<Transfer>(`/stock-transfers/${transferId}/${action}`, {
    method: "POST",
    ...(body === undefined ? {} : { body }),
    ifMatch,
    idempotencyKey,
  });
  return { data, etag };
}

export function shipTransfer(
  transferId: string,
  body: TransferPostingRequest | undefined,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<Transfer>> {
  return postTransferTransition(transferId, "ship", body, ifMatch, idempotencyKey);
}

export function receiveTransfer(
  transferId: string,
  body: TransferPostingRequest | undefined,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<Transfer>> {
  return postTransferTransition(transferId, "receive", body, ifMatch, idempotencyKey);
}

export function cancelTransfer(
  transferId: string,
  body: TransferPostingRequest | undefined,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<Transfer>> {
  return postTransferTransition(transferId, "cancel", body, ifMatch, idempotencyKey);
}

// ── Cycle counts ─────────────────────────────────────────────────────────

export async function listCountWorksheets(
  query: CountListQuery,
  signal?: AbortSignal,
): Promise<Page<CountWorksheetSummary>> {
  return toMutablePage(
    await apiFetch<CountPage>("/count-worksheets", { query: { ...query }, signal }),
  );
}

/**
 * Blind entry representation. `view=entry` is the counter-facing payload:
 * snapshot and variance quantities are absent from the wire, not hidden in
 * the client. The review payload lives behind its own permission.
 */
export function getCountWorksheetEntry(worksheetId: string): Promise<WithEtag<CountWorksheet>> {
  return fetchWithEtag<CountWorksheet>(`/count-worksheets/${worksheetId}`, {
    query: { view: "entry" },
  });
}

/** Permission-gated review representation (snapshot + variance revealed). */
export function getCountWorksheetReview(
  worksheetId: string,
): Promise<WithEtag<CountWorksheetReview>> {
  return fetchWithEtag<CountWorksheetReview>(`/count-worksheets/${worksheetId}`, {
    query: { view: "review" },
  });
}

export function createCountWorksheet(
  input: CountWorksheetCreate,
  idempotencyKey: string,
): Promise<CountWorksheet> {
  return apiFetch<CountWorksheet>("/count-worksheets", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export async function startCountWorksheet(
  worksheetId: string,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<CountWorksheet>> {
  const { data, etag } = await apiRequest<CountWorksheet>(
    `/count-worksheets/${worksheetId}/start`,
    { method: "POST", ifMatch, idempotencyKey },
  );
  return { data, etag };
}

export async function submitCountWorksheet(
  worksheetId: string,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<CountWorksheet>> {
  const { data, etag } = await apiRequest<CountWorksheet>(
    `/count-worksheets/${worksheetId}/submit`,
    { method: "POST", ifMatch, idempotencyKey },
  );
  return { data, etag };
}

export async function cancelCountWorksheet(
  worksheetId: string,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<CountWorksheet>> {
  const { data, etag } = await apiRequest<CountWorksheet>(
    `/count-worksheets/${worksheetId}/cancel`,
    { method: "POST", ifMatch, idempotencyKey },
  );
  return { data, etag };
}

/** Incremental blind entry: counter-supplied quantities only. */
export async function recordCountEntries(
  worksheetId: string,
  batch: CountEntryBatch,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<CountEntryBatchResult>> {
  const { data, etag } = await apiRequest<CountEntryBatchResult>(
    `/count-worksheets/${worksheetId}/entries`,
    { method: "POST", body: batch, ifMatch, idempotencyKey },
  );
  return { data, etag };
}

/** Single-line correction — a re-count, never a local undo. */
export async function recordCountLine(
  worksheetId: string,
  countLineId: string,
  entry: CountLineEntry,
  ifMatch: string,
): Promise<WithEtag<CountLineBlind>> {
  const { data, etag } = await apiRequest<CountLineBlind>(
    `/count-worksheets/${worksheetId}/lines/${countLineId}`,
    { method: "PATCH", body: entry, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

export async function approveCountWorksheet(
  worksheetId: string,
  body: CountApprovalRequest | undefined,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<CountApprovalResult>> {
  const { data, etag } = await apiRequest<CountApprovalResult>(
    `/count-worksheets/${worksheetId}/approve`,
    { method: "POST", ...(body === undefined ? {} : { body }), ifMatch, idempotencyKey },
  );
  return { data, etag };
}

export async function rejectCountWorksheet(
  worksheetId: string,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<CountWorksheetReview>> {
  const { data, etag } = await apiRequest<CountWorksheetReview>(
    `/count-worksheets/${worksheetId}/reject`,
    { method: "POST", ifMatch, idempotencyKey },
  );
  return { data, etag };
}

// ── Analytics ────────────────────────────────────────────────────────────

/** `days` is REQUIRED by the contract — omitting it is a 400, never a default. */
export function getDeadStockReport(
  query: DeadStockQuery,
  signal?: AbortSignal,
): Promise<DeadStockPage> {
  return apiFetch<DeadStockPage>("/reports/dead-stock", { query: { ...query }, signal });
}

export function getSlowMoversReport(
  query: SlowMoverQuery,
  signal?: AbortSignal,
): Promise<SlowMoverPage> {
  return apiFetch<SlowMoverPage>("/reports/slow-movers", { query: { ...query }, signal });
}

/** CSV export path for the dead-stock report (opened in a new tab). */
export function deadStockExportHref(query: DeadStockExportQuery): string {
  const search = new URLSearchParams();
  search.set("days", String(query.days));
  if (query.group_by !== undefined) search.set("group_by", query.group_by);
  if (query.warehouse_id !== undefined) search.set("warehouse_id", query.warehouse_id);
  if (query.category_id !== undefined) search.set("category_id", query.category_id);
  if (query.branch_id !== undefined) search.set("branch_id", query.branch_id);
  return `/api/v1/reports/dead-stock/export?${search.toString()}`;
}

export function getVariantStats(
  variantId: string,
  query: VariantStatsQuery,
  signal?: AbortSignal,
): Promise<VariantStats> {
  return apiFetch<VariantStats>(`/variants/${variantId}/inventory-stats`, {
    query: { ...query },
    signal,
  });
}

/** Batch / serial lookup for batch-tracked movement and transfer lines. */
export async function lookupBatches(
  query: BatchLookupQuery,
  signal?: AbortSignal,
): Promise<Batch[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<BatchPage>("/batches", {
        query: { limit: 200, ...query, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

// ── Cross-domain lookups ─────────────────────────────────────────────────
// Inventory commands require catalog identifiers (`variant_id`, `uom_id`) and
// the branch that owns a warehouse. These reads belong to other lanes'
// resources, so every caller permission-gates them; the transport is still
// typed from the same generated client.

type VariantPage =
  operations["catalogListVariants"]["responses"][200]["content"]["application/json"];
type UomPage = operations["catalogListUoms"]["responses"][200]["content"]["application/json"];
type CategoryPage =
  operations["catalogListCategories"]["responses"][200]["content"]["application/json"];
type BranchPage = operations["listBranches"]["responses"][200]["content"]["application/json"];

export type VariantOption = Schemas["CatalogVariant"];
export type UomOption = Schemas["CatalogUom"];
export type CategoryOption = Schemas["CatalogCategory"];
export type BranchOption = Schemas["Branch"];

/**
 * SKU picker search. `q` is the catalog's published citext picker filter, but
 * the deployed catalog service still answers 400 to it (see the feature
 * report's backend-gap note). Rather than break every keystroke, a refused
 * filter degrades to the unfiltered active page narrowed client-side; the
 * moment the service accepts `q`, the first call succeeds and the fallback
 * stops running.
 */
export async function searchVariants(q: string, signal?: AbortSignal): Promise<VariantOption[]> {
  const base = { limit: 50, status: "active" } as const;
  if (q === "") {
    return [...(await apiFetch<VariantPage>("/variants", { query: { ...base }, signal })).items];
  }
  try {
    return [...(await apiFetch<VariantPage>("/variants", { query: { ...base, q }, signal })).items];
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 400) throw error;
    const page = await apiFetch<VariantPage>("/variants", { query: { ...base }, signal });
    const needle = q.toLocaleLowerCase();
    return page.items.filter((variant) => variant.sku.toLocaleLowerCase().includes(needle));
  }
}

/**
 * Exact barcode lookup for the scan box (a unique filter → at most one item).
 * The catalog service does not implement this published filter yet, so callers
 * must treat a failure as "lookup unavailable", not as "unknown barcode".
 */
export async function lookupVariantByBarcode(
  barcode: string,
  signal?: AbortSignal,
): Promise<VariantOption[]> {
  const page = await apiFetch<VariantPage>("/variants", { query: { barcode, limit: 2 }, signal });
  return [...page.items];
}

export async function listActiveUoms(signal?: AbortSignal): Promise<UomOption[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<UomPage>("/uoms", {
        query: { limit: 200, status: "active", ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export async function listCategories(signal?: AbortSignal): Promise<CategoryOption[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<CategoryPage>("/categories", {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export async function listBranches(signal?: AbortSignal): Promise<BranchOption[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<BranchPage>("/branches", {
        query: { limit: 200, status: "active", ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}
